import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";

// Date-wise report of wallet recharge (top-up) requests: one row per request
// date with counts and amounts by status. The backend applies the same scoping
// as the request list, so LMBP users only see their own requests.

const toInputDate = (d) => d.toISOString().slice(0, 10);
const toApiDate = (v) => (v ? v.split("-").reverse().join("-") : ""); // yyyy-mm-dd → dd-mm-yyyy

const rupees = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const COLUMNS = [
  { key: "total", label: "Total" },
  { key: "approved", label: "Approved" },
  { key: "pending", label: "Pending" },
  { key: "rejected", label: "Rejected" },
];

const DateWiseRechargeReport = () => {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return toInputDate(d);
  });
  const [toDate, setToDate] = useState(() => toInputDate(new Date()));
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosPostApi(apiList.MAIN_WALLET_DATE_SUMMARY, {
        username: sessionStorage.getItem("username"),
        fromDate: toApiDate(fromDate),
        toDate: toApiDate(toDate),
      });
      if (response?.data?.respCode === "00") {
        setReport(response.data.data);
      } else {
        setReport(null);
        setError(response?.data?.message || "Could not load the report.");
      }
    } catch (e) {
      setReport(null);
      setError("Could not load the report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadExcel = () => {
    const toRow = (r, date) => {
      const row = { Date: date };
      COLUMNS.forEach((c) => {
        row[`${c.label} Requests`] = r[`${c.key}Count`];
        row[`${c.label} Amount (₹)`] = Number(r[`${c.key}Amount`] || 0);
      });
      return row;
    };
    const rows = [
      ...report.days.map((d) => toRow(d, d.date)),
      toRow(report.totals, "Total"),
    ];
    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Date-wise Recharge");
    XLSX.writeFile(
      book,
      `wallet-recharge-${toApiDate(fromDate)}-to-${toApiDate(toDate)}.xlsx`,
    );
  };

  const days = report?.days || [];

  return (
    <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[16px] py-[14px] mt-4">
      <div className="d-flex flex-wrap align-items-end justify-content-between" style={{ gap: "12px" }}>
        <h5 className="mb-0 font-medium">Date-wise Recharge Report</h5>
        <div className="d-flex flex-wrap align-items-end" style={{ gap: "10px" }}>
          <label style={{ fontSize: "12px", color: "#6b7280" }}>
            From
            <input
              type="date"
              className="d-block px-2 h-[36px] border border-gray-300 rounded-lg"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label style={{ fontSize: "12px", color: "#6b7280" }}>
            To
            <input
              type="date"
              className="d-block px-2 h-[36px] border border-gray-300 rounded-lg"
              value={toDate}
              min={fromDate}
              max={toInputDate(new Date())}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn btn-outline-secondary"
            style={{ height: "36px" }}
            onClick={fetchReport}
            disabled={loading || !fromDate || !toDate}
          >
            {loading ? "Loading…" : "Show"}
          </button>
          <button
            type="button"
            className="btn"
            style={{ height: "36px", background: "#FF702B", color: "#fff" }}
            onClick={downloadExcel}
            disabled={!days.length}
          >
            Excel Download
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-3" style={{ color: "#dc2626", fontSize: "14px" }}>
          {error}
        </div>
      )}

      {report && (
        <div className="table-responsive mt-3">
          <table className="table table-sm align-middle mb-0" style={{ fontSize: "14px" }}>
            <thead>
              <tr>
                <th rowSpan={2}>Date</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} colSpan={2} className="text-center">
                    {c.label}
                  </th>
                ))}
              </tr>
              <tr>
                {COLUMNS.map((c) => (
                  <React.Fragment key={c.key}>
                    <th className="text-end">Requests</th>
                    <th className="text-end">Amount (₹)</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.length === 0 ? (
                <tr>
                  <td colSpan={1 + COLUMNS.length * 2} className="text-center text-muted py-3">
                    No wallet recharge requests in this period.
                  </td>
                </tr>
              ) : (
                days.map((d) => (
                  <tr key={d.date}>
                    <td>{d.date}</td>
                    {COLUMNS.map((c) => (
                      <React.Fragment key={c.key}>
                        <td className="text-end">{d[`${c.key}Count`]}</td>
                        <td className="text-end">{rupees(d[`${c.key}Amount`])}</td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            {days.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 600 }}>
                  <td>Total</td>
                  {COLUMNS.map((c) => (
                    <React.Fragment key={c.key}>
                      <td className="text-end">{report.totals[`${c.key}Count`]}</td>
                      <td className="text-end">{rupees(report.totals[`${c.key}Amount`])}</td>
                    </React.Fragment>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default DateWiseRechargeReport;
